import { Check, FileText, Layers, PenTool, Cpu, Workflow, Rocket, ClipboardList, Gauge } from "lucide-react";
import { win } from "@/components/ScrollRail";
import LottiePlayer from "@/components/LottiePlayer";
import cycleAnim from "@/assets/lottie/process-cycle.json";
import networkAnim from "@/assets/lottie/network-nodes.json";
import rippleAnim from "@/assets/lottie/reach-ripple.json";
import { STAGES } from "@/components/ProcessLoop";

const fmt = (n, d = 0) => n.toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

/* ------------------------------------------------------------------ */
/* 1. Project files that load as you scroll (What We Do / House of Projects) */
export const PROJECT_FILES = [
  [FileText, "01_Discovery-brief.pdf", "Problem, constraints, goals"],
  [Workflow, "02_Workflow-map.fig", "How work and data move today"],
  [Layers, "03_System-architecture.md", "Structure the business runs on"],
  [PenTool, "04_UI-UX-prototype.fig", "Screens for customers and teams"],
  [Cpu, "05_Platform-build/", "Web platform and custom logic"],
  [Workflow, "06_Automation-flows.json", "Handoffs replaced, exceptions routed"],
  [Rocket, "07_Launch-checklist.xlsx", "QA, go-live and rollout"],
  [Gauge, "08_Metrics-dashboard", "Measure, refine, repeat"],
];

export function FilesRail({ p }) {
  const n = PROJECT_FILES.length;
  const rows = PROJECT_FILES.map((f, i) => ({ f, k: win(p, 0.03 + i * 0.105, 0.03 + i * 0.105 + 0.16) }));
  const done = rows.filter((r) => r.k >= 1).length;
  return (
    <div data-arm="hop" className="hp-rail-card">
      <div className="flex items-center justify-between">
        <p className="hp-rail-title">Project files</p>
        <p className="hp-rail-meta" data-testid="files-count">{done} / {n} ready</p>
      </div>
      <div className="hp-rail-track mt-3"><div className="hp-rail-fill" style={{ transform: `scaleX(${done / n + (rows.find((r) => r.k > 0 && r.k < 1)?.k || 0) / n})` }} /></div>
      <ul className="mt-4 space-y-2">
        {rows.map(({ f: [Icon, name, sub], k }) => (
          <li key={name} className={`hp-file ${k > 0 ? "on" : ""}`}>
            <span className="hp-file-ico"><Icon className="w-4 h-4" aria-hidden="true" /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13.5px] font-bold text-hop-blue truncate">{name}</span>
              <span className="hp-file-sub block text-[12px] text-slate-500 truncate">{sub}</span>
              <span className="hp-file-bar"><span style={{ transform: `scaleX(${k})` }} /></span>
            </span>
            <span className="hp-file-state">{k >= 1 ? <Check className="w-4 h-4" aria-hidden="true" /> : `${Math.round(k * 100)}%`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Social reach that climbs as you scroll (uses figures already published on the site) */
export function ReachRail({ p }) {
  const views = 5.7 * win(p, 0.04, 0.6);
  const yt = 1.0 * win(p, 0.25, 0.75);
  const leads = Math.round(30 * win(p, 0.4, 0.85));
  const pieces = Math.round(50 * win(p, 0.1, 0.5));
  const draw = 1 - win(p, 0.02, 0.9);
  return (
    <div data-arm="ae" className="hp-rail-card hp-rail-card-dark">
      <div className="flex items-center justify-between">
        <p className="hp-rail-title text-white">Reach, building</p>
        <div className="w-14 h-14 -my-3 -mr-2"><LottiePlayer data={rippleAnim} /></div>
      </div>
      <div className="mt-2">
        <div className="font-display text-6xl font-medium text-white tabular-nums" data-testid="reach-views">{fmt(views, 1)}M<span className="text-3xl">+</span></div>
        <p className="text-[13px] text-slate-300 mt-1">Instagram views in 3.5 months, one founder-led account</p>
      </div>
      <svg viewBox="0 0 300 110" className="mt-4 w-full" role="img" aria-label="Reach curve rising">
        {[25, 55, 85].map((y) => <line key={y} x1="0" x2="300" y1={y} y2={y} stroke="rgba(255,255,255,.12)" />)}
        <path pathLength="1" d="M0 100 L40 92 L80 96 L120 70 L160 76 L200 48 L245 32 L300 8" fill="none" stroke="#7fa2ff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1" strokeDashoffset={draw} />
        <circle cx={300 - 300 * draw} cy={100 - 92 * (1 - draw)} r="5" fill="#fff" opacity={draw < 0.99 ? 1 : 0} />
      </svg>
      <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-white/15 pt-4">
        <div><dt className="text-[11px] text-slate-400">YouTube views</dt><dd className="font-display text-2xl text-white tabular-nums">{fmt(yt, 1)}M+</dd></div>
        <div><dt className="text-[11px] text-slate-400">Inbound leads</dt><dd className="font-display text-2xl text-white tabular-nums">{leads}+</dd></div>
        <div><dt className="text-[11px] text-slate-400">Pieces / month</dt><dd className="font-display text-2xl text-white tabular-nums">{pieces}+</dd></div>
      </dl>
      <p className="mt-4 text-[11px] text-slate-400 leading-snug">Results come from individual engagements and are not a guarantee of future outcomes.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Candidate-to-offer pipeline (Hiring) */
export function PipelineRail({ p, steps }) {
  const active = Math.min(steps.length - 1, Math.floor(p * steps.length * 0.999));
  return (
    <div data-arm="ak" className="hp-rail-card">
      <div className="flex items-center justify-between">
        <p className="hp-rail-title">Profile to offer</p>
        <p className="hp-rail-meta">Step {active + 1} of {steps.length}</p>
      </div>
      <div className="mx-auto mt-2 w-44 h-44"><LottiePlayer data={networkAnim} label="Candidates and employers connected through one portal" /></div>
      <ol className="mt-3 space-y-2">
        {steps.map(([t, d], i) => (
          <li key={t} className={`hp-step ${i === active ? "active" : i < active ? "past" : ""}`}>
            <span className="hp-step-n">{i < active ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : i + 1}</span>
            <span className="min-w-0"><span className="block text-[14px] font-bold text-hop-blue">{t}</span><span className="block text-[12.5px] text-slate-500">{d}</span></span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 4. Operating loop tracker (How We Work) */
export function LoopRail({ p }) {
  const active = Math.min(STAGES.length - 1, Math.floor(p * STAGES.length * 0.999));
  const cols = ["var(--arm-hop)", "#5b4bd6", "var(--arm-ae)", "var(--arm-ak)"];
  return (
    <div data-arm="hop" className="hp-rail-card">
      <div className="flex items-center justify-between">
        <p className="hp-rail-title">Operating loop</p>
        <p className="hp-rail-meta">{STAGES[active].n}</p>
      </div>
      <div className="mx-auto mt-2 w-52 h-52"><LottiePlayer data={cycleAnim} label="Research, diagnose, execute, optimise cycle" /></div>
      <ol className="mt-3 grid grid-cols-2 gap-2">
        {STAGES.map((s, i) => (
          <li key={s.n} className="rounded border px-3 py-2 text-[13px] font-bold transition-colors"
            style={{ borderColor: i === active ? cols[i] : "#e2e8f0", color: i === active ? "#fff" : "#475569", background: i === active ? cols[i] : "#fff" }}>
            {i + 1}. {s.n}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 5. India market numbers (Why Now). Percent-of-population figures are the ones already quoted on the page. */
export function NumbersRail({ p }) {
  const rows = [
    ["Internet users", 70, "70% of population", "1.03B"],
    ["Social media identities", 34.1, "34.1% of population", "500M"],
    ["Growth in one year", 27.7, "+27.7% new users", "+223M"],
  ];
  return (
    <div className="hp-rail-card hp-rail-card-dark">
      <div className="flex items-center justify-between">
        <p className="hp-rail-title text-white">India online</p>
      </div>
      <ul className="mt-5 space-y-6">
        {rows.map(([l, pct, sub, big], i) => {
          const k = win(p, 0.05 + i * 0.2, 0.45 + i * 0.2);
          return (
            <li key={l}>
              <div className="flex items-baseline justify-between">
                <span className="text-[13px] font-semibold text-slate-300">{l}</span>
                <span className="font-display text-3xl text-white tabular-nums">{big}</span>
              </div>
              <div className="mt-2 h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-[#7fa2ff]" style={{ width: `${pct * k}%` }} /></div>
              <p className="mt-1.5 text-[12px] text-slate-400">{sub}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 6. Engagement checklist (About / principles) */
export function PrinciplesRail({ p, items }) {
  const active = Math.min(items.length - 1, Math.floor(p * items.length * 0.999));
  return (
    <div data-arm="hop" className="hp-rail-card">
      <div className="flex items-center justify-between"><p className="hp-rail-title">How we operate</p><ClipboardList className="w-5 h-5 text-[var(--hop-brand)]" aria-hidden="true" /></div>
      <ul className="mt-4 space-y-2">
        {items.map(([t], i) => (
          <li key={t} className={`hp-step ${i === active ? "active" : i < active ? "past" : ""}`}>
            <span className="hp-step-n">{i < active ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : i + 1}</span>
            <span className="text-[14px] font-bold text-hop-blue">{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
